import React, { useContext, useEffect, useRef } from "react";

// SASS
import "./Part2.scss";

// COMPONENTS
import Step from "../Step/Step";

// CONTEXT
import BuildContext from "../../contexts/BuildContext/BuildContext";

// HELPERS
import animate from "../../utils/animate";

// LIBRARIES
import scrollama from "scrollama";

// ASSETS
import dropImg from "../../assets/img/droplet-1.png";
import drop2Img from "../../assets/img/droplet-2.png";
import nurseProfileImg from "../../assets/img/nurse-2-profile.png";
import arrowImg from "../../assets/img/arrow.svg";
import breathImg from "../../assets/img/breath.svg";

export default function Part2({ step }) {
  const scrolly = scrollama();

  // ANIMATION STEP DATA
  const animationSequence = [
    { stepNumber: "0", height: null, description: "fade in text box" },
    { stepNumber: "1", height: null, description: "fade out text box" },
    { stepNumber: "2", height: null, description: "fade in profile scene" },
    { stepNumber: "3", height: null, description: "convert droplet to drop" },
    { stepNumber: "4", height: null, description: "move drop to nose" },
    { stepNumber: "5", height: null, description: "hide drop, show puff" },
    {
      stepNumber: "6",
      height: null,
      description: "hide puff, show small drop scene",
    },
    { stepNumber: "7", height: null, description: "convert droplet to drop" },
    {
      stepNumber: "8",
      height: null,
      description: "move drop, 500ms timeout, show bottom text",
    },
    { stepNumber: "9", height: null, description: "show wind" },
    { stepNumber: "10", height: null, description: "move drop" },
    { stepNumber: "11", height: null, description: "spacer" },
  ];

  const { buildEnv } = useContext(BuildContext);

  const textBoxEl = useRef(null);
  const profileSceneEl = useRef(null);
  const roundDrop1El = useRef(null);
  const ovalDrop1El = useRef(null);
  const roundDrop2El = useRef(null);
  const ovalDrop2El = useRef(null);
  const smallDropSceneEl = useRef(null);
  const bottomTextBlockEl = useRef(null);
  const breathEl = useRef(null);
  const arrowEl = useRef(null);

  useEffect(() => {
    scrolly
      .setup({
        step: `.${step}`,
        debug: false,
        threshold: 1,
        progress: true,
      })
      .onStepEnter(({ index, direction }) => {
        switch (index) {
          case 0: {
            animate.fadeInOnEnter(textBoxEl.current, direction);
            break;
          }
          case 1: {
            animate.fadeOutOnEnter(textBoxEl.current, direction);

            break;
          }
          case 2: {
            animate.fadeInOnEnter(profileSceneEl.current, direction);
            break;
          }
          case 3: {
            animate.fadeOutOnEnter(roundDrop1El.current, direction);
            animate.fadeInOnEnter(ovalDrop1El.current, direction);
            break;
          }
          case 4: {
            animate.addClassOnEnter(ovalDrop1El.current, "moved", direction);
            break;
          }
          case 5: {
            //add show puff
            animate.fadeOutOnEnter(ovalDrop1El.current, direction);
            animate.fadeInOnEnter(breathEl.current, direction);
            break;
          }
          case 6: {
            animate.fadeOutOnEnter(breathEl.current, direction);
            animate.fadeInOnEnter(smallDropSceneEl.current, direction);
            break;
          }
          case 7: {
            animate.fadeOutOnEnter(roundDrop2El.current, direction);
            animate.fadeInOnEnter(ovalDrop2El.current, direction);
            break;
          }
          case 8: {
            animate.addClassOnEnter(ovalDrop2El.current, "moved", direction);
            setTimeout(() => {
              animate.fadeInOnEnter(bottomTextBlockEl.current, direction);
            }, 500);
            break;
          }
          case 9: {
            animate.fadeInOnEnter(arrowEl.current, direction);
            break;
          }
          case 10: {
            //animate.addClassOnEnter(ovalDrop2El.current, "moved", direction);
            setTimeout(() => {
              animate.addClassOnEnter(
                ovalDrop2El.current,
                "moved-2",
                direction
              );
            }, 500);
            break;
          }
          default:
            break;
        }
      })
      .onStepProgress(({ index, progress }) => {
        switch (index) {
          default:
            break;
        }
      })
      .onStepExit(({ index, direction }) => {
        switch (index) {
          case 0: {
            animate.fadeOutOnExit(textBoxEl.current, direction);
            break;
          }
          case 1: {
            animate.fadeInOnExit(textBoxEl.current, direction);

            break;
          }
          case 2: {
            animate.fadeOutOnExit(profileSceneEl.current, direction);
            break;
          }
          case 3: {
            animate.fadeInOnExit(roundDrop1El.current, direction);
            animate.fadeOutOnExit(ovalDrop1El.current, direction);
            break;
          }
          case 4: {
            animate.removeClassOnExit(ovalDrop1El.current, "moved", direction);
            break;
          }
          case 5: {
            // add hide puff
            animate.fadeOutOnExit(breathEl.current, direction);
            animate.fadeInOnExit(ovalDrop1El.current, direction);
            break;
          }
          case 6: {
            animate.fadeInOnExit(breathEl.current, direction);
            animate.fadeOutOnExit(smallDropSceneEl.current, direction);
            break;
          }
          case 7: {
            animate.fadeInOnExit(roundDrop2El.current, direction);
            animate.fadeOutOnExit(ovalDrop2El.current, direction);
            break;
          }
          case 8: {
            animate.fadeOutOnExit(bottomTextBlockEl.current, direction);

            setTimeout(() => {
              animate.removeClassOnExit(
                ovalDrop2El.current,
                "moved",
                direction
              );
            }, 500);
            break;
          }
          case 9: {
            animate.fadeOutOnExit(arrowEl.current, direction);
            break;
          }
          case 10: {
            animate.removeClassOnExit(
              ovalDrop2El.current,
              "moved-2",
              direction
            );
            break;
          }
          default:
            break;
        }
      });
  }, [scrolly, step]);

  const showStepText = (buildEnv, text) => {
    if (buildEnv === "development") {
      return text;
    } else return null;
  };

  return (
    <section className="wrapper two">
      <div className="sticky-container">
        {/* Sticky contents in here */}
        <div className="part2">
          <div className="text-box__container" ref={textBoxEl}>
            <div className="text-box__text">
              When you touch something with a live virus on it and then touch
              your face, without cleaning your hands first, the virus can get
              into your <br />
              <b>eyes, nose, and mouth.</b>
              <div className="drop drop-one">
                <img src={dropImg} alt="Droplet" className="img-fluid" />
              </div>
              <div className="drop drop-two">
                <img src={dropImg} alt="Droplet" className="img-fluid" />
              </div>
              <div className="drop drop-three">
                <img src={dropImg} alt="Droplet" className="img-fluid" />
              </div>
              <div className="drop drop-four">
                <img src={dropImg} alt="Droplet" className="img-fluid" />
              </div>
            </div>
          </div>
          <div className="profile-scene" ref={profileSceneEl}>
            <div className="profile-scene__text profile-scene__text--top">
              To understand how, let’s look at how respiratory droplets travel.
            </div>
            <div className="profile-scene__profile">
              <img
                src={nurseProfileImg}
                alt="Profile of nurse"
                className="img-fluid"
              />
              <div className="drop round-drop round-drop-1" ref={roundDrop1El}>
                <img src={dropImg} alt="Droplet" className="img-fluid" />
              </div>
              <div className="drop round-drop oval-drop-1" ref={ovalDrop1El}>
                <img src={drop2Img} alt="Droplet" className="img-fluid" />
              </div>
            </div>
          </div>
          <div className="small-drop-scene" ref={smallDropSceneEl}>
            <div className="small-drop-scene__text-box small-drop-scene__text-box-1">
              When someone exhales, some of the tiny droplets in their breath
              get caught in the air.
              <div className="drop round-drop round-drop-1">
                <img src={dropImg} alt="Drop" className="img-fluid" />
              </div>
              <div className="drop round-drop round-drop-2">
                <img src={dropImg} alt="Drop" className="img-fluid" />
              </div>
              <div className="drop round-drop round-drop-3">
                <img src={dropImg} alt="Drop" className="img-fluid" />
              </div>
              <div className="drop round-drop round-drop-4">
                <img src={dropImg} alt="Drop" className="img-fluid" />
              </div>
              <div className="drop round-drop round-drop-5">
                <img src={dropImg} alt="Drop" className="img-fluid" />
              </div>
              <div className="drop round-drop round-drop-6">
                <img src={dropImg} alt="Drop" className="img-fluid" />
              </div>
              <div className="drop round-drop round-drop-7" ref={roundDrop2El}>
                <img src={dropImg} alt="Drop" className="img-fluid" />
              </div>
              <div className="drop oval-drop oval-drop-1" ref={ovalDrop2El}>
                <img src={drop2Img} alt="Drop" className="img-fluid" />
              </div>
            </div>
          </div>
          <div className="bottom-text-block" ref={bottomTextBlockEl}>
            Then they get carried away, traveling wherever the air currents go.
          </div>
          <div className="breath-img" ref={breathEl}>
            <img src={breathImg} alt="Breath" className="img-fluid" />
          </div>
          <div className="arrow-img" ref={arrowEl}>
            <img src={arrowImg} alt="Arrow" className="img-fluid" />
          </div>
        </div>
      </div>
      {/* STEPS */}
      {animationSequence.map(({ stepNumber, height, description, offset }) => {
        return (
          <Step
            stepSection={step}
            stepNumber={stepNumber}
            stepDescription={description}
            height={height}
            key={stepNumber}
            offset={offset}
          />
        );
      })}
    </section>
  );
}
